/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputDropdownComponent} from '../../shared/input/input-dropdown/input-dropdown.component';
import {SupplyingPackingPlantBaseModel} from '../../api/ms-specialorders/contracts/supplying-packing-plant-base.model';
import {
    AbstractControl,
    ValidatorFn
} from '@angular/forms';
import {objectsValidator} from '../../shared/input/input-validators';
import {RouteSearchCriteriaModel} from '../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {PartnerBaseModel} from '../../api/ms-specialorders/contracts/partner-base.model';
import {ShipToPartyBaseModel} from '../../api/ms-specialorders/contracts/ship-to-party-base.model';
import {
    Observable,
    of,
    Subject,
    Subscription
} from 'rxjs';
import {TypeaheadHelperService} from '../../shared/services/typeahead-helper.service';
import {ReferenceDataApi} from '../../api/ms-partnumberchanges/reference-data.api';
import {InputMessagesService} from '../../shared/input/service/input-messages.service';
import {MandatoryMessageTypeEnum} from '../../shared/input/service/mandatory-message-type.enum';
import {ReferenceDataModel} from '../../api/reference-data.model';
import {InputComponent} from '../../shared/input/input/input.component';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {WarningsEnum} from '../../shared/warnings/warnings.enum';
import {TooltipEnum} from '../../shared/tooltip/tooltip.enum';
import {PartNumberChangesCredentialsStateModel} from './part-number-changes-credentials-state.model';
import {InputDisplayModeEnum} from '../../shared/input/input-display-mode.enum';
import {PncTypeEnum} from '../models/pnc-type-enum/pnc-type.enum';
import {PartNumberChangesCredentialsConfigurationModel} from '../services/part-number-changes-detail-configuration/models/part-number-changes-credentials-configuration.model';
import {PartNumberChangeStatusTimelineConfigurationModel} from '../models/part-number-change-status-timeline-configuration.model';
import {StatusTimelineConfigurationModel} from '../../shared/status-timeline/models/status-timeline-configuration.model';
import {PncDetailModel} from '../models/pnc-detail/pnc-detail.model';
import {PartNumberChangeTypeBaseModel} from '../../api/ms-partnumberchanges/contracts/pnc-type/model/part-number-change-type-base.model';
import {StateConfigurationModel} from '../../shared/status-timeline/models/state-configuration.model';
import {HistoryStateConfigurationModel} from '../../shared/status-timeline/models/history-state-configuration.model';
import {PartNumberChangeStatusTimelineHistoryModel} from '../models/part-number-change-status-timeline-history.model';
import {PncStatusColorEnum} from '../models/pnc-status-color.enum';
import {PartNumberChangesStatusEnum} from '../models/part-number-changes-status.enum';
import {PartNumberChangesCreateNewService} from "../services/part-number-changes-create-new.service";

@Component({
    selector: 'app-part-number-changes-credentials',
    templateUrl: './part-number-changes-credentials.component.html',
    styleUrls: ['../styles/pnc-base.component.css', './part-number-changes-credentials.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesCredentialsComponent
        extends StatefulComponent<PartNumberChangesCredentialsStateModel>
        implements OnInit, OnDestroy, AfterViewInit {

    @Input() credentials: PartNumberChangesCredentialsConfigurationModel;
    @Input() sectionTitle: string;
    @Input() statusTimeline: PartNumberChangeStatusTimelineConfigurationModel;
    @Input() pncType: PncTypeEnum;
    @Input() triggerResetForm: Subject<void> = new Subject<void>();

    /** Inputs variables */
    @ViewChild('partnerInput') partnerInput: InputDropdownComponent;
    @ViewChild('shipToPartyInput') shipToPartyInput: InputDropdownComponent;
    @ViewChild('supplyingPackingPlantInput') supplyingPackingPlantInput: InputDropdownComponent;

    /** Outputs variables */
    @Output() partNumberChangesCredentials = new Subject<PartNumberChangesCredentialsStateModel>();
    @Output() validForCreate = new Subject<boolean>();
    @Output() isLoading = new Subject<boolean>();

    @Output() routeSearchCriteriaChange: Subject<RouteSearchCriteriaModel> =
            new Subject<RouteSearchCriteriaModel>();
    public routeSearchCriteria: RouteSearchCriteriaModel;

    public WarningsEnum = WarningsEnum;
    public TooltipEnum = TooltipEnum;
    /** Inputs validators */
    validators: { [key: string]: ValidatorFn[] } = {
        partner: [objectsValidator],
        shipToParty: [objectsValidator],
        supplyingPackingPlant: [objectsValidator]
    };
    /** Inputs messages variables */
    public partnerInputMessages = new Map<WarningsEnum, string[]>();
    public shipToPartyInputMessages = new Map<WarningsEnum, string[]>();
    public supplyingPackingPlantInputMessages = new Map<WarningsEnum, string[]>();

    private partnerOptions: PartnerBaseModel[] = [];
    private shipToPartyOptions: ShipToPartyBaseModel[] = [];
    private supplyingPackingPlantOptions: SupplyingPackingPlantBaseModel[] = [];
    private subscriptions = new Subscription();
    readonly labelRegion = 'Country/Region';
    private isLastPNCStateEEAnalysis = false;

    get partnerSectionClass(): string {
        let styleClass = 'pnc-credentials-partner-section';

        if (this.isViewMode()) {
            styleClass += ' view-mode';
        } else {
            styleClass += ' create-mode';
        }

        return styleClass;
    }

    get supplyingPackingPlantClass(): string {
        let styleClass = 'pnc-credentials-plant-section';

        if (!this.isViewMode()) {
            styleClass += ` create-mode`;
        }
        return styleClass;
    }

    get hasAlternateBackground(): boolean {
        return this.pncType === PncTypeEnum.PARTNER || this.pncType === PncTypeEnum.AI_CHANGE;
    }

    constructor(
            public typeahead: TypeaheadHelperService,
            private referenceDataApi: ReferenceDataApi,
            private inputMessagesService: InputMessagesService,
            private partNumberChangesCreateNewService: PartNumberChangesCreateNewService,
            private changeDetector: ChangeDetectorRef) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(this.triggerResetForm.subscribe(() => this.resetForm()));
    }

    ngAfterViewInit(): void {
        this.toggleInputFields();
        this.getPartners();
        this.changeDetector.detectChanges();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    isViewMode(): boolean {
        let viewMode = false;

        if (this.credentials != null) {
            viewMode = this.credentials.displayMode === InputDisplayModeEnum.VIEW;
        }
        return viewMode;
    }

    toggleInputFields: () => void = (): void => {
        this.supplyingPackingPlantInput.disable();
        this.shipToPartyInput.disable();
    };

    /**
     * Partner input filter function
     */
    searchPartners = (term: string): Observable<PartnerBaseModel[]> => {
        if (term != null && term.length > 0) {
            return of(this.partnerOptions.filter((item) => (`${item.code} - ${item.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.partnerOptions);
    };

    /**
     * Ship to party input filter function
     */
    searchShipToParty = (term: string): Observable<ShipToPartyBaseModel[]> => {
        if (term != null && term.length > 0) {
            return of(this.shipToPartyOptions.filter((shipToParty) => (`${shipToParty.code} - ${shipToParty.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.shipToPartyOptions);
    };

    /**
     * Supplying packing plant input filter function
     */
    searchSupplyingPackingPlants = (term: string): Observable<ShipToPartyBaseModel[]> => {
        if (term != null && term.length > 0) {
            return of(this.supplyingPackingPlantOptions.filter((plant) => (`${plant.code} - ${plant.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.supplyingPackingPlantOptions);
    };

    /**
     * Sets the new partner value
     * Calls {@link partNumberChangesCredentials.emit()} to emit new header values
     * Calls {@link getShipToPartyOptions} to get the corresponding ship to party options
     */
    setPartnerValue(partner: PartnerBaseModel): void {
        this.checkInvalidInputs(partner, this.state.partner, this.shipToPartyInput);

        if (this.isValidCodeOption(partner)) {
            this.getActiveShipToParties(partner.code);
        }

        if (partner?.code !== this.state?.partner?.code) {
            this.routeSearchCriteriaChange.next({
                partnerCode: partner?.code,
                shipToPartyCode: this.state?.shipToParty?.code,
                supplyingPackingPlantCode: this.state?.supplyingPackingPlant?.code
            });
        }

        this.storeState({partner});
        this.partNumberChangesCredentials.next(this.state);
        this.validForCreate.next(this.isValidForCreate());
        this.setShipToPartyValue(this.state.shipToParty);
    }

    /**
     * Sets the new ship to party value
     * Calls {@link getSupplyingPackingPlantOptions} to get the corresponding supplying packing plant options from the backend
     * Calls {@link partNumberChangesCredentials.emit()} to emit new updatedPartNumberChangeData
     */
    setShipToPartyValue(shipToParty: ShipToPartyBaseModel): void {
        this.checkInvalidInputs(shipToParty, this.state.shipToParty, this.supplyingPackingPlantInput);

        if (this.isValidCodeOption(shipToParty)) {
            this.getSupplyingPackingPlantOptions(this.state.partner?.code, shipToParty.code);
        }

        if (shipToParty?.code !== this.state?.shipToParty?.code) {
            this.routeSearchCriteriaChange.next({
                partnerCode: this.state?.partner?.code,
                shipToPartyCode: shipToParty?.code,
                supplyingPackingPlantCode: this.state?.supplyingPackingPlant?.code
            });
        }

        this.storeState({shipToParty});
        this.partNumberChangesCredentials.next(this.state);
        this.validForCreate.next(this.isValidForCreate());
        this.setSupplyingPackingPlantValue(this.state.supplyingPackingPlant);
    }

    /**
     * Sets the new supplying packing plant value
     * Calls {@link getTransportModeOptions} to get the corresponding transport mode options from the backend
     * Calls {@link partNumberChangesCredentials.emit()} to emit new updatedPartNumberChangeData
     */
    setSupplyingPackingPlantValue(plant: SupplyingPackingPlantBaseModel): void {
        if (plant?.code !== this.state?.supplyingPackingPlant?.code) {
            this.routeSearchCriteriaChange.next({
                partnerCode: this.state?.partner?.code,
                shipToPartyCode: this.state?.shipToParty?.code,
                supplyingPackingPlantCode: plant?.code
            });
        }

        this.storeState({supplyingPackingPlant: plant});
        this.partNumberChangesCredentials.next(this.state);
        this.validForCreate.next(this.isValidForCreate());
    }

    /**
     * Manages the partner input status changes, adding errors and warnings as needed
     */
    managePartnerInputStateChange(partner: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(partner,
                this.partnerInputMessages,
                MandatoryMessageTypeEnum.CREATE);
        this.inputMessagesService.invalidInputValueError(partner,
                this.partnerInputMessages,
                this.partnerInput?.label);
        this.partnerInputMessages = new Map(this.partnerInputMessages);
    }

    /**
     * Manages the ship to party input status changes, adding errors and warnings as needed
     */
    manageShipToPartyInputStateChange(shipToParty: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(shipToParty,
                this.shipToPartyInputMessages,
                MandatoryMessageTypeEnum.CREATE);
        this.inputMessagesService.invalidInputValueError(shipToParty,
                this.shipToPartyInputMessages,
                this.shipToPartyInput?.label);
        this.shipToPartyInputMessages = new Map(this.shipToPartyInputMessages);
    }

    /**
     * Manages the supplying packing plant input status changes, adding errors and warnings as needed
     */
    manageSupplyingPackingPlantInputStateChange(plant: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(plant,
                this.supplyingPackingPlantInputMessages,
                MandatoryMessageTypeEnum.CREATE);
        this.inputMessagesService.invalidInputValueError(plant, this.supplyingPackingPlantInputMessages,
                this.supplyingPackingPlantInput?.label);
        this.supplyingPackingPlantInputMessages = new Map(this.supplyingPackingPlantInputMessages);
    }

    /**
     * Converts the PartNumberChangePreviousStatusModel[] into a StatusTimelineConfigurationModel
     */
    mapStatusTimelineToConfigurationModel(): StatusTimelineConfigurationModel {
        const lastStateCode = [...(this.statusTimeline.timelineHistory ?? [])].pop()?.status?.code;
        const configuration: StatusTimelineConfigurationModel = {
            backgroundColor: this.statusTimeline.statusColors.get(PncStatusColorEnum.BACKGROUND_COLOR),
            happyPath: this.getStatusTimelineHappyPath(this.credentials.pncType),
            statusHistory: this.statusHistoryMapper(this.statusTimeline.timelineHistory),
            collapseAtLevel: 2,
            showAllStatesOnLastLine: this.showAllStatesOnLastLineHandler(lastStateCode),
            widthPercentage: this.dynamicTimelineWidth(lastStateCode)
        };

        return configuration;
    }

    private showAllStatesOnLastLineHandler(statusCode: string): boolean {
        // Should show all states on last line if the last State is on Status REJECTED or CANCELED
        return statusCode === PartNumberChangesStatusEnum.REJECTED || statusCode === PartNumberChangesStatusEnum.CANCELLED;
    }

    private dynamicTimelineWidth(statusCode: string): number {
        let timelineWidth = 100;
        if (statusCode === PartNumberChangesStatusEnum.CANCELLED) {
            timelineWidth = 90;
        }
        return timelineWidth;
    }

    private statusHistoryMapper(pncHistory: PartNumberChangeStatusTimelineHistoryModel[]): HistoryStateConfigurationModel[] {
        // requiresFlashing comes on pncDetail and it is previously mapped on the first element
        const isRequiresFlashingEnable = [...pncHistory].shift().requiresFlashing;
        if (isRequiresFlashingEnable) {
            pncHistory = this.deleteRepeatedAnalysisStates(pncHistory);
        }
        return this.getStatusHistory(pncHistory);
    }

    private deleteRepeatedAnalysisStates(pncHistory: PartNumberChangeStatusTimelineHistoryModel[]):
            PartNumberChangeStatusTimelineHistoryModel[] {
        const updatedPncHistory: PartNumberChangeStatusTimelineHistoryModel[] = [];

        pncHistory.forEach((elem: PartNumberChangeStatusTimelineHistoryModel, index) => {
            // Merge an Analysis state into the MAIN Analysis (to avoid duplicates)
            if (elem.status.code === PartNumberChangesStatusEnum.ANALYSIS) {
                // If the last state isn't Analysis then we should add first of all an Analysis state
                // so that will allow the next states to be merged into this MAIN state.
                if ([...updatedPncHistory].pop().status.code !== PartNumberChangesStatusEnum.ANALYSIS) {
                    updatedPncHistory.push(elem);
                } else {
                    // If we already have the last state Analysis then the merge can proceed
                    this.mergeAnalysisStates(updatedPncHistory, elem);
                }
                // Always merge an EE Analysis state because it should not be displayed directly on the timeline, only a different color
                // should inform the user that it is an EE.
            } else if (elem.status.code === PartNumberChangesStatusEnum.EE_ANALYSIS) {
                this.mergeAnalysisStates(updatedPncHistory, elem);

                if (index === pncHistory.length - 1) {
                    this.isLastPNCStateEEAnalysis = true;
                }
            } else {
                updatedPncHistory.push(elem);
            }
        });

        return updatedPncHistory;
    }

    /**
     * Merges relevant information from an given state into the last state from the passed array
     */
    private mergeAnalysisStates(elements: PartNumberChangeStatusTimelineHistoryModel[],
                                element: PartNumberChangeStatusTimelineHistoryModel):
            PartNumberChangeStatusTimelineHistoryModel[] {

        let lastAnalysisState = [...elements].pop();
        lastAnalysisState = {
            ...lastAnalysisState,
            status: {
                ...lastAnalysisState.status,
                code: PartNumberChangesStatusEnum.ANALYSIS,
                name: 'Analysis'
            },
            createdOn: element.createdOn
        };
        elements[elements.length - 1] = lastAnalysisState;

        return elements;
    }

    private getStatusHistory(pncHistory: PartNumberChangeStatusTimelineHistoryModel[]): HistoryStateConfigurationModel[] {
        const historyStateConfig: HistoryStateConfigurationModel[] = [];
        let analysisCounter = 1;
        let rejectedCounter = 1;
        let currentStatusCounter: number;

        pncHistory.forEach((elem: PartNumberChangeStatusTimelineHistoryModel, index) => {
            const stateCode = this.checkDynamicCreationState(elem.status.code);

            // Add counters to status ANALYSIS and REJECTED to be displayed in Timeline
            if (elem.status.code === PartNumberChangesStatusEnum.ANALYSIS) {
                currentStatusCounter = analysisCounter++;
            } else if (elem.status.code === PartNumberChangesStatusEnum.REJECTED) {
                currentStatusCounter = rejectedCounter++;
            } else {
                currentStatusCounter = null;
            }

            const config: HistoryStateConfigurationModel = {
                state: {
                    name: elem.status.name,
                    code: elem.status.code,
                    color: this.getStateColor(stateCode, index === pncHistory.length - 1),
                    initialState: index === 0,
                    finalState: this.isStateFinal(elem.status.code),
                    repeatable: elem.status.code === PartNumberChangesStatusEnum.ANALYSIS,
                    count: currentStatusCounter,
                    flag: elem.flag,
                    nextStatus: this.getNextStatusStringifyInformation(pncHistory, index + 1)
                },
                nextStateCode: this.getNextPNCState(PartNumberChangesStatusEnum.ANALYSIS, pncHistory, index + 1)
                        ? PartNumberChangesStatusEnum.ANALYSIS : null,
                timestamp: elem.createdOn
            };

            historyStateConfig.push(config);
        });

        return historyStateConfig;
    }

    /**
     * If isLastState is an EE Analysis state then it should be displayed on the timeline with the default color
     * otherwise should be colored with the respective status colors.
     */
    private getStateColor(code: string, isLastState: boolean): string {
        let color = this.statusTimeline.statusColors.get(<PncStatusColorEnum>code);
        if (isLastState && this.isLastPNCStateEEAnalysis) {
            color = undefined;
        }
        return color;
    }

    private isStateFinal(code: string): boolean {
        return code === PartNumberChangesStatusEnum.SOLVED || code === PartNumberChangesStatusEnum.CANCELLED;
    }

    /**
     * Returns information about the next state - (rejected), '(accepted)' or null - to displayed it on the timeline
     */
    private getNextStatusStringifyInformation(array: PartNumberChangeStatusTimelineHistoryModel[], nextStateIndex: number): string {
        let nextStatus = null;

        if (this.getNextPNCState(PartNumberChangesStatusEnum.REJECTED, array, nextStateIndex)) {
            nextStatus = `(${array[nextStateIndex].status.code.toLowerCase()})`;
        } else if (this.getNextPNCState(PartNumberChangesStatusEnum.SOLVED, array, nextStateIndex)) {
            nextStatus = '(accepted)';
        }
        return nextStatus;
    }

    private checkDynamicCreationState(stateCode: string): string {
        const creationStateCodes = [PncTypeEnum.PLANNER, PncTypeEnum.PARTNER, PncTypeEnum.F2_MISSING_PARTS, PncTypeEnum.AI_CHANGE];
        let creationState = stateCode;
        if (creationStateCodes.find((elem) => elem === stateCode)) {
            creationState = 'CREATION';
        }
        return creationState;
    }

    private getNextPNCState(pncEnum: PartNumberChangesStatusEnum, array: PartNumberChangeStatusTimelineHistoryModel[],
                            nextStateIndex: number): boolean {
        let nextState = false;

        if (array[nextStateIndex] && array[nextStateIndex].status.code === pncEnum) {
            nextState = true;
        }

        return nextState;
    }

    /**
     * For a given PCN detail returns its status timeline happy path model
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getStatusTimelineHappyPath(pncType: PartNumberChangeTypeBaseModel): StateConfigurationModel[] {
        const happyPath: StateConfigurationModel[] = [
            {
                code: 'SOLVED',
                name: 'Solved',
                color: this.statusTimeline.statusColors.get(PncStatusColorEnum.SOLVED),
                finalState: true
            }
        ];

        if (pncType.code !== PncTypeEnum.AI_CHANGE) {
            happyPath.unshift({
                code: 'ANALYSIS',
                name: 'Analysis',
                color: this.statusTimeline.statusColors.get(PncStatusColorEnum.ANALYSIS),
                repeatable: true
            });
        }

        return happyPath;
    }

    private getPartners(): void {
        if (!this.isViewMode()) { // TODO: CTWILS-5025 - Remove if when input component is improved
            this.routeSearchCriteria = {partnerCode: null, shipToPartyCode: null, supplyingPackingPlantCode: null};
            this.isLoading.next(true);
            this.subscriptions.add(
                    this.referenceDataApi.getActivePartners(this.routeSearchCriteria)
                            .subscribe((partners: PartnerBaseModel[]) => {
                                this.partnerOptions = partners;
                                if (partners.length === 1) {
                                    if (this.state.partner == null
                                            || this.isDifferentCodeOption(this.state.partner, partners[0])) {
                                        this.partnerInput.setValue(partners[0]);
                                    }
                                    this.partnerInput.disable();
                                } else if (partners.length > 1 && this.partnerInput.isDisabled) {
                                    this.partnerInput.enable();
                                }
                            })
                            .add(() => {
                                this.isLoading.next(false);
                            })
            );
        }
    }

    private getActiveShipToParties(partner: string): void {
        if (!this.isViewMode()) { // TODO: CTWILS-5025 - Remove if when input component is improved
            this.routeSearchCriteria = {partnerCode: partner, supplyingPackingPlantCode: null, shipToPartyCode: null};
            this.isLoading.next(true);

            this.subscriptions.add(
                    this.referenceDataApi.getActiveShipToParties(this.routeSearchCriteria)
                            .subscribe((shipToParties: ShipToPartyBaseModel[]) => {
                                this.shipToPartyOptions = shipToParties;
                                if (shipToParties.length === 1) {
                                    if (this.state.shipToParty == null
                                            || this.isDifferentCodeOption(this.state.shipToParty, shipToParties[0])) {
                                        this.shipToPartyInput.setValue(shipToParties[0]);
                                    }
                                    this.shipToPartyInput.disable();
                                } else if (shipToParties.length > 1 && this.shipToPartyInput.isDisabled) {
                                    this.shipToPartyInput.enable();
                                }
                            })
                            .add(() => {
                                this.isLoading.next(false);
                            })
            );
        }
    }

    private getSupplyingPackingPlantOptions = (partner: string, shipToParty: string): void => {
        if (!this.isViewMode()) { // TODO: CTWILS-5025 - Remove if when input component is improved
            this.routeSearchCriteria = {
                partnerCode: partner,
                supplyingPackingPlantCode: null,
                shipToPartyCode: shipToParty
            };
            this.isLoading.next(true);

            this.subscriptions.add(
                    this.referenceDataApi.getActiveSupplyingPackingPlants(this.routeSearchCriteria)
                            .subscribe((plant: SupplyingPackingPlantBaseModel[]) => {
                                this.supplyingPackingPlantOptions = plant;
                                this.partNumberChangesCreateNewService.sendAlternativeSppsToCreatePncDto(plant);
                                if (plant.length === 1) {
                                    this.supplyingPackingPlantInput.setValue(plant[0]);
                                    this.supplyingPackingPlantInput.disable();
                                } else if (plant.length > 1 && this.supplyingPackingPlantInput.isDisabled) {
                                    this.supplyingPackingPlantInput.enable();
                                }
                            })
                            .add(() => {
                                this.isLoading.next(false);
                            })
            );
        }
    };

    private checkInvalidInputs(oldEntity: ReferenceDataModel,
                               newEntity: ReferenceDataModel,
                               input: InputComponent,
                               validateIfIsDifferentCodeOption = true): void {
        if (oldEntity == null || !this.isValidCodeOption(oldEntity)
                || (validateIfIsDifferentCodeOption ? this.isDifferentCodeOption(oldEntity, newEntity) : false)) {
            input.setValue(null, {emitEvent: false});
            input.disable();
        }
    }

    /**
     * Determines is a option that contains a code attribute is valid. if the code is option, valid is considered.
     * @param option the option to check
     * @param optional the flag optional
     * @returns boolean if the option is valid
     */
    private isValidCodeOption = (option: any, optional: boolean = false): boolean =>
            option != null && option !== '' && (optional || option.code != null);

    /**
     * Determines if two objects have different code attributes
     * @param value1 the 1st object value
     * @param value2 the 2nd object value
     * @returns boolean if the objects have the same code attribute value
     */
    private isDifferentCodeOption(value1: any, value2: any): boolean {
        let isDifferent = true;

        if (value1 != null && value2 != null &&
                value1.constructor === value2.constructor &&
                value1.code != null && value2.code != null &&
                value1.code === value2.code) {
            isDifferent = false;
        }

        return isDifferent;
    }

    /**
     * Checks if the form is valid for save
     * @returns boolean if the global form is valid for save
     */
    private isValidForCreate(): boolean {
        return this.formInputsIsValid()
                && this.state.partner != null
                && this.state.shipToParty != null
                && this.state.supplyingPackingPlant?.name != null;
    }

    /**
     * Checks if every input is valid
     * @returns boolean if all the inserted inputs values are valid
     */
    private formInputsIsValid(): boolean {
        return this.partnerInput?.isValid
                && this.shipToPartyInput?.isValid
                && this.supplyingPackingPlantInput?.isValid;
    }

    private resetForm = (): void => {
        this.partnerInput.reset();
        this.shipToPartyInput.reset();
        this.supplyingPackingPlantInput.reset();
        this.storeState({
            partner: null,
            shipToParty: null,
            supplyingPackingPlant: null
        });
    };

}
